import React from "react";
import { clsx } from "clsx";
import { Filter } from "@usereactify/search";

import { FilterSectionBox } from "~/components/Filter/FilterSectionBox";
import { FilterSectionList } from "~/components/Filter/FilterSectionList";
import { withCollectionHeroBannerSizeFilters } from "./withCollectionHeroBannerSizeFilters";

import { Button, CustomImage } from "@bared/ui";

const RteWrapper = (props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "[&_.rte]:flex [&_.rte]:flex-col",
      props.contentAlignment === "left"
        ? "[&_.rte]:items-start"
        : "[&_.rte]:items-center",
    )}
  />
);

const SizeWrapper = (props: any) => (
  <>
    <div
      {...props}
      className={clsx(
        props.className,
        "collection-hero-banner-size-wrapper",
        "flex flex-wrap [&_ul]:py-micro",
        "left" === props.contentAlignment
          ? "[&_ul]:justify-start"
          : "[&_ul]:justify-center",
        "[&_li]:p-0 [&_li]:w-[auto]",
        "[&_label]:p-0 [&_label]:leading-h1",
        "[&_.size]:flex [&_.size]:h-[56px] [&_.size]:w-[72px] [&_.size]:items-center [&_.size]:justify-center",
      )}
    />
    <style>{`
      .collection-hero-banner-size-wrapper .size {
        border: none;
        ${props.textColor ? `color: ${props.textColor};` : ""}
        ${props.bgColor ? `background-color: ${props.bgColor};` : ""}
      }

      .collection-hero-banner-size-wrapper .size:hover, .collection-hero-banner-size-wrapper .size[aria-current="true"] {
        ${props.hoverTextColor ? `color: ${props.hoverTextColor};` : ""}
        ${props.hoverBgColor ? `background-color: ${props.hoverBgColor};` : ""}
      }

      .collection-hero-banner-size-wrapper .size span {
        font-size: 11px;
      }
    `}</style>
  </>
);

const CategoryWrapper = (props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "[&_ul]:pt-0 [&_.category_li]:my-0 [&_.category_li_button]:mb-0 flex [&_.category]:flex [&_.category]:flex-wrap [&_.category]:items-baseline [&_.category_li]:mx-pico [&_.category_li_label]:capitalize [&_ul]:pb-micro",
      "left" === props.contentAlignment
        ? "[&_.category]:justify-start"
        : "[&_.category]:justify-center",
    )}
  />
);

const CollectionHeroBannerSizeFilters = withCollectionHeroBannerSizeFilters(
  ({
    active,
    bannerBackgroundColor,
    categoryFilter,
    collectionTitle,
    collectionLinkTitle,
    contentAlignment,
    desktopBanner,
    desktopImage,
    faqHeading,
    handleCategoryClick,
    handleViewAllClick,
    hideCollectionTitle,
    link,
    mobileBanner,
    mobileImage,
    sizeFilter,
    sizeFilterBackgroundColor,
    sizeFilterHoverBackgroundColor,
    sizeFilterHoverTextColor,
    sizeFilterTextColor,
    sizeFilterTitle,
  }) => {
    const { rgb } = bannerBackgroundColor;

    return (
      <div className="md:px-0 flex flex-col items-center px-midi pb-mini pt-maxi sm:py-[40px]">
        {!!desktopImage ? (
          <div className="absolute bottom-zero left-zero right-zero top-zero hidden h-full w-full md:block">
            <CustomImage
              data={desktopImage}
              imageClassName="object-cover object-center h-full"
            />
          </div>
        ) : null}

        {!!mobileImage ? (
          <div className="absolute bottom-zero left-zero right-zero top-zero block h-full w-full md:hidden">
            <CustomImage
              data={mobileImage}
              imageClassName="object-cover object-center h-full"
            />
          </div>
        ) : null}

        {rgb ? (
          <div
            className="absolute top-zero h-full w-full"
            style={{
              backgroundColor: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})`,
            }}
          />
        ) : null}

        <div
          className={clsx(
            "left" === contentAlignment
              ? "items-start md:pl-midi"
              : "items-center",
            "z-10 flex w-full flex-col",
          )}
        >
          <div className="hidden md:block">
            <RteWrapper contentAlignment={contentAlignment}>
              {desktopBanner}
            </RteWrapper>
          </div>
          <div className="block md:hidden">
            <RteWrapper contentAlignment={contentAlignment}>
              {mobileBanner}
            </RteWrapper>
          </div>

          {!hideCollectionTitle ? (
            <h1 className="my-mini font-body text-h4 font-medium leading-h4">
              {collectionTitle}
            </h1>
          ) : null}

          <p className="font-body text-body font-bold uppercase leading-body">
            {sizeFilterTitle}
          </p>
          <SizeWrapper
            contentAlignment={contentAlignment}
            bgColor={sizeFilterBackgroundColor?.hex}
            textColor={sizeFilterTextColor?.hex}
            hoverBgColor={sizeFilterHoverBackgroundColor?.hex}
            hoverTextColor={sizeFilterHoverTextColor?.hex}
          >
            {sizeFilter && (
              <Filter
                filter={sizeFilter}
                renderFilterList={({ filterListProps }) => (
                  <FilterSectionBox
                    className="size"
                    layout="heroBanner"
                    {...filterListProps}
                  />
                )}
                showEmptyFilters={true}
              />
            )}
          </SizeWrapper>

          <p className="font-body text-body font-bold uppercase leading-body">
            {collectionLinkTitle}
          </p>
          <CategoryWrapper contentAlignment={contentAlignment}>
            {categoryFilter && (
              <Filter
                filter={categoryFilter}
                renderFilterList={({ filterListProps }) => (
                  <FilterSectionList
                    className="category"
                    hideProductCount={true}
                    layout="heroBanner"
                    handleViewAllClick={handleViewAllClick}
                    handleCategoryClick={handleCategoryClick}
                    viewAllActive={active}
                    {...filterListProps}
                  />
                )}
                showEmptyFilters={true}
              />
            )}
          </CategoryWrapper>
          {link?.url ? (
            <div className="flex items-baseline" data-testid="faq-link-wrapper">
              <p className="font-body text-hint leading-hint">
                {faqHeading}&nbsp;
              </p>
              <Button
                variant="link"
                href={link.url}
                className="text-white hover:text-white"
              >
                {link.title}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

export default CollectionHeroBannerSizeFilters;
